import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { errorMessage, formatDateTime } from "helpers/common";
import { api } from "helpers/axios";
import sanitize from "helpers/sanitize";
import ErrorAlert from "components/ui/ErrorAlert";
import ImportDetailHeader from "components/ui/ImportDetailHeader";
import ImportDetailTabs from "components/ui/ImportDetailTabs";
import ImportReportMenu from "./ImportReportMenu";
import { toast } from "helpers/toast";
import { autoAssignHeaders } from "helpers/importFieldMatching";
import { useTranslation } from "translation/useTranslation";
import { DndContext, DragOverlay, useDraggable, useDroppable } from "@dnd-kit/core";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchFields, getAllCustomNames, getAllFields } from "store/fieldsSlice";
import { buildImportOverviewPath, IMPORT_TABS } from "./importNavigation";
import { POLLABLE_STATUSES, getDefaultDetailTab, operationAlts, operationStatusAlts } from "./importHelpers";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";
import { Button } from "components/ui";

const AVAILABLE_FIELDS = [
  { key: 'first_name', translation_key: 'field.first_name', label: 'First Name', required: true, values_for_selected: 'firstname,first name,first_name,vorname' },
  { key: 'last_name', translation_key: 'field.last_name', label: 'Last Name', required: true, values_for_selected: 'lastname,last name,last_name,nachname' },
  { key: 'email', translation_key: 'field.email', label: 'Email', required: true, values_for_selected: 'email,mail,e-mail' },
  // The number decides who the person is (LIX-1809); same aliases as the backend and the employee import.
  { key: 'employee_number', translation_key: 'field.employee_number', label: 'Employee Number', required: true, values_for_selected: 'employee_number,employee number,employeenumber,employee_no,employeeid,employee id,mitarbeiternummer,pers-nr,pers-nummer,personalnummer' },
];

const recordRowClass = status => ({ failed: 'table-danger', conflicted: 'table-warning' })[status] ?? '';
const recordBadgeClass = status => ({ imported: 'badge-success', failed: 'badge-danger' })[status] ?? 'badge-warning';

function Draggable({ id, originalId = id, active = false, handleManualRemove = null, fieldMapping = null }) {
  const { isDragging, attributes, listeners, setNodeRef, transform } = useDraggable({ id });

  const counter = useMemo(() => {
    if (!fieldMapping) return 0;
    let count = 0;
    for (const k in fieldMapping) {
      if (fieldMapping[k] === originalId) count++;
    }
    return count;
  }, [fieldMapping, originalId]);

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div
      className={`import-draggable ${active ? 'active' : ''} ${counter > 0 ? 'has-count' : ''}`}
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
    >
      <span>{originalId}</span>
      {counter > 1 && <span className="counter-span">{counter}</span>}
      {active && (
        <Button
          variant="ghost"
          size="inline"
          data-no-dnd="true"
          icon="bi-x"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={handleManualRemove}
          data-testid={`remove-mapping-${originalId}`}
        />
      )}
    </div>
  );
}

function Droppable({ field, item, handleManualRemove, fieldCustomNames = {} }) {
  const { translate, editableTranslate } = useTranslation();
  const { isOver, setNodeRef } = useDroppable({ id: field.key });

  return (
    <div className={`import-droppable ${isOver ? 'on-hover' : ''} ${item ? 'has-item' : ''}`} ref={setNodeRef}>
      <div className="import-droppable-header">
        <span>{fieldCustomNames?.[field.translation_key] || editableTranslate(field.translation_key, field.label)}</span>
        {field.required && (
          <span className="required-badge">{editableTranslate('user_import.required', 'PFLICHT')}</span>
        )}
      </div>
      <div className="import-droppable-value">
        {item
          ? (
            <Draggable
              id={`slot-${field.key}-${item}`}
              originalId={item}
              active={true}
              handleManualRemove={() => handleManualRemove(field.key)}
            />
          )
          : translate('user_import.drag_field_name_here', 'Feldname hierher ziehen...')
        }
      </div>
    </div>
  );
}

export default function UserImportForm({ id, setMode = null, forTable = false, fetchUserImports = null, backTo = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { translate, editableTranslate } = useTranslation();

  const allFields = useSelector(getAllFields);
  const fieldCustomNames = useSelector(getAllCustomNames);

  const [importData, setImportData] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [fieldMapping, setFieldMapping] = useState({});
  const [loadingImportData, setLoadingImportData] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeDetailTab, setActiveDetailTab] = useState("details");

  const [resizedCount, setResizedCount] = useState(0);

  const tableRef = useRef(null);

  const handleReturnToOverview = useCallback(() => {
    if (typeof backTo === "string") {
      navigate(backTo);
      return;
    }

    if (setMode) {
      setMode("view");
      return;
    }

    navigate(buildImportOverviewPath(`tab=${IMPORT_TABS.loginUsers}`));
  }, [backTo, location.pathname, navigate, setMode]);

  const fetchImportData = useCallback(async ({ silent = false, background = false } = {}) => {
    if (!silent) setLoadingImportData(true);
    try {
      const { data, status } = await api.get(`/user-imports/${id}`, { meta: { background } });
      if (status === 200) {
        setImportStatus(data.status);
        setImportData(data);

        const fieldMappingKeyed = {};
        (data.field_mapping || []).forEach(mapping => {
          fieldMappingKeyed[mapping.field] = mapping.header;
        });

        if (data.status === 'draft') {
          const autoMapped = autoAssignHeaders(AVAILABLE_FIELDS, data.headers ?? [], translate);
          for (const [key, header] of Object.entries(autoMapped)) {
            if (!fieldMappingKeyed[key]) fieldMappingKeyed[key] = header;
          }
        }

        setFieldMapping(fieldMappingKeyed);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetchImportData'));
    }
    if (!silent) setLoadingImportData(false);
  }, [id, translate]);

  const fetchImportRecords = useCallback(async () => {
    setLoadingRecords(true);
    try {
      const { data, status } = await api.get(`/user-imports/${id}/records`);
      if (status === 200) {
        setRecords(data.data);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetchImportRecords'));
    }
    setLoadingRecords(false);
  }, [id]);

  useEffect(() => {
    if (!allFields) dispatch(fetchFields());

    if (id) {
      fetchImportData();
    }
  }, [allFields, dispatch, id, fetchImportData]);

  useEffect(() => {
    if (importStatus && !POLLABLE_STATUSES.includes(importStatus)) {
      if (['completed', 'partially_completed', 'failed'].includes(importStatus)) {
        fetchImportRecords();
      }
    }
  }, [fetchImportRecords, importStatus]);

  // Poll while importing
  useEffect(() => {
    if (!importStatus || !POLLABLE_STATUSES.includes(importStatus)) return;

    const intervalId = setInterval(() => {
      fetchImportData({ silent: true, background: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [importStatus, fetchImportData]);

  useEffect(() => {
    if (!forTable && importStatus) {
      setActiveDetailTab(getDefaultDetailTab(importStatus));
    }
  }, [forTable, importStatus]);

  useEffect(() => {
    setResizedCount(c => c + 1);
  }, [records]);

  // --- DnD handlers ---

  const parseDraggableId = (draggableId) => {
    if (draggableId.startsWith('source-')) return draggableId.slice(7);
    // slot-{fieldKey}-{header}: field keys have no hyphens (only underscores)
    return draggableId.replace(/^slot-[^-]+-/, '');
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over) return;
    const header = parseDraggableId(active.id);
    setFieldMapping(prev => ({ ...(prev || {}), [over.id]: header }));
  };

  const handleManualRemove = (fieldKey) => {
    setFieldMapping(prev => {
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const handleAutoAssign = () => {
    const mapped = autoAssignHeaders(AVAILABLE_FIELDS, importData?.headers ?? [], translate);
    setFieldMapping(prev => ({ ...(prev || {}), ...mapped }));
  };

  const mappedCount = useMemo(
    () => Object.values(fieldMapping || {}).filter(Boolean).length,
    [fieldMapping]
  );

  const reverseFieldMapping = useMemo(() => {
    const result = [];
    for (const [fieldKey, header] of Object.entries(fieldMapping || {})) {
      if (!header) continue;
      const field = AVAILABLE_FIELDS.find(f => f.key === fieldKey);
      if (field) result.push({ header, label: field.label, translation_key: field.translation_key });
    }
    return result;
  }, [fieldMapping]);

  // --- Action handlers ---

  const handleSaveFieldMapping = async () => {
    setLoading(true);
    setError('');
    try {
      const fieldMappingArray = AVAILABLE_FIELDS
        .filter(f => fieldMapping[f.key])
        .map(f => ({
          field: f.key,
          header: fieldMapping[f.key],
          overwrite: true,
          extra_data: null,
        }));

      await api.post(`/user-imports/${id}/field-mapping`, { field_mapping: fieldMappingArray });
      toast.success(editableTranslate('user_import.field_mapping_saved', 'Feldzuordnung gespeichert.'));
      fetchImportData();
    } catch (err) {
      setError(errorMessage(err, 'handleSaveFieldMapping'));
    }
    setLoading(false);
  };

  const handleProcess = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post(`/user-imports/${id}/process`);
      toast.success(editableTranslate('user_import.processing_started', 'Importverarbeitung gestartet.'));
      fetchImportData();
    } catch (err) {
      setError(errorMessage(err, 'handleProcess'));
    }
    setLoading(false);
  };

  const handleCancel = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post(`/user-imports/${id}/cancel`);
      if (fetchUserImports) fetchUserImports();
      handleReturnToOverview();
    } catch (err) {
      setError(errorMessage(err, 'handleCancel'));
    }
    setLoading(false);
  };

  const handleDraft = async () => {
    setLoading(true);
    setError('');
    try {
      await api.post(`/user-imports/${id}/draft`);
      fetchImportData();
    } catch (err) {
      setError(errorMessage(err, 'handleDraft'));
    }
    setLoading(false);
  };

  const isDraft = importStatus === 'draft';
  const isReadyForImport = importStatus === 'ready_for_import';
  const isProcessing = POLLABLE_STATUSES.includes(importStatus);
  const isFinished = ['completed', 'partially_completed', 'failed'].includes(importStatus);
  const detailStats = useMemo(() => ([
    {
      label: editableTranslate('user_import.total_records', 'Gesamtdatensätze'),
      value: importData?.total_records ?? '-',
    },
    {
      label: editableTranslate('user_import.imported_records', 'Importiert'),
      value: importData?.imported_records_count ?? 0,
      valueClassName: 'text-success',
    },
    {
      label: editableTranslate('user_import.failed_records', 'Fehlgeschlagen'),
      value: importData?.failed_records_count ?? 0,
      valueClassName: 'text-danger',
    },
  ]), [editableTranslate, importData]);
  const detailTabs = useMemo(() => ([
    { key: "details", label: editableTranslate("user_import.details", "Quellen-Details") },
    { key: "mapping", label: editableTranslate("user_import.field_assignment", "Zuordnung") },
    { key: "records", label: editableTranslate("user_import.records", "Datensätze") },
  ]), [editableTranslate]);

  if (loadingImportData) {
    return (
      <div className="d-flex justify-content-center py-5" data-testid="loading">
        <div className="spinner-border" role="status"><span className="sr-only">Wird geladen...</span></div>
      </div>
    );
  }

  if (!importData) return null;

  return (
    <div className={forTable ? '' : ''}>
      {error && (
        <div className="mb-3">
          <ErrorAlert error={error} onClose={() => setError(null)} />
        </div>
      )}

      {!forTable && (
        <ImportDetailHeader
          backTo={backTo}
          backLabel={editableTranslate('user_import.back_to_login_user_imports', 'Zurück zu Login-Benutzer-Importen')}
          iconClass="bi bi-person-badge"
          title={
            <>
              {editableTranslate('user_import.type_login_users', 'Login-Benutzer')} {editableTranslate('user_import.import', 'Import')}
            </>
          }
          status={importStatus}
          statusLabel={editableTranslate('user_import.status.' + importStatus, importData?.status_formatted)}
          metadata={[
            { label: editableTranslate('user_import.original_file_name', 'Dateiname'), value: importData?.original_file_name },
            { label: editableTranslate('user_import.hash_id', 'Hash ID'), value: importData?.hash_id },
            { label: editableTranslate('user_import.created_at', 'Erstellt am'), value: formatDateTime(importData?.date_formatted?.created_at) },
          ]}
          stats={detailStats}
          progress={isProcessing ? importData?.progress ?? 0 : null}
          actions={isFinished && importData?.capabilities?.can_export ? (
            <ImportReportMenu
              endpoint={`/user-imports/${id}/report/export`}
              fallbackFilename="user_import_report"
              rejectedFilter={(importData?.failed_records_count ?? 0) > 0 ? { statuses: ["failed", "conflicted"] } : null}
              rejectedLabel={editableTranslate("import_report.rejected", "Nur fehlerhafte Zeilen")}
              onError={setError}
            />
          ) : null}
        />
      )}

      {!forTable && (
        <ImportDetailTabs
          className="mb-4"
          tabs={detailTabs}
          activeTab={activeDetailTab}
          onChange={setActiveDetailTab}
        />
      )}

      {/* Details panel */}
      {forTable ? (
        <div className="gray-box-3">
          <div className="row">
            <div className="col">
              <span>
                <span className="text2-color">
                  {editableTranslate('user_import.file_name', 'Dateiname')}:
                </span>
                <span
                  className="text-body-color ml-3"
                  data-tooltip-id="import-hash-id-tooltip"
                  data-tooltip-content={importData?.hash_id}
                >
                  {importData?.original_file_name}
                </span>
              </span>
            </div>
          </div>
        </div>
      ) : activeDetailTab === "details" ? (
        <div className="gray-box mt-4">
          <label className="question-label font-weight-bold">
            {editableTranslate('user_import.details', 'Quellen-Details')}
          </label>
          <div className="px-3 py-2 pb-3">
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="peb-field__label">
                    {editableTranslate('user_import.original_file_name', 'Dateiname')}
                  </label>
                  <p>{importData?.original_file_name}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="peb-field__label">
                    {editableTranslate('user_import.hash_id', 'Hash ID')}
                  </label>
                  <p>{importData?.hash_id}</p>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="peb-field__label">
                    {editableTranslate('user_import.records', 'Datensätze')}
                  </label>
                  <p>
                    {importData?.imported_records_count ?? 0} / {importData?.total_records ?? '-'}&nbsp;
                    {editableTranslate('user_import.imported', 'importiert')}
                  </p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="peb-field__label">
                    {editableTranslate('user_import.created_at', 'Erstellt am')}
                  </label>
                  <p>{formatDateTime(importData?.date_formatted?.created_at)}</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="peb-field__label">
                    {editableTranslate('user_import.status', 'Status')}
                  </label>
                  <p>
                    <span className={`badge ${['completed', 'partially_completed'].includes(importStatus) ? 'badge-success' : importStatus === 'failed' ? 'badge-danger' : 'badge-warning'}`}>
                      {editableTranslate('user_import.status.' + importStatus, importData?.status_formatted)}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Field mapping — draft: drag-and-drop */}
      {(forTable || activeDetailTab === "mapping") && isDraft && (
        <div className="mt-4">
          <DndContext
            onDragStart={(e) => setActiveId(e.active.id)}
            onDragEnd={(e) => {
              setActiveId(null);
              handleDragEnd(e);
            }}
          >
            <div className="import-headers-div">
              <div className="d-flex justify-content-between align-items-center">
                <span className="font-weight-bold">
                  {editableTranslate('user_import.available_fields_drag', 'Verfügbare Felder (zum Zuordnen ziehen)')}
                </span>
                <Button variant="secondary" onClick={handleAutoAssign} data-testid="auto-assign-button">
                  {editableTranslate('user_import.automatically_assign', 'Automatisch zuordnen')}
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-2 mt-3">
                {(importData?.headers || []).map(header => (
                  <Draggable
                    key={header}
                    id={`source-${header}`}
                    originalId={header}
                    fieldMapping={fieldMapping}
                  />
                ))}
              </div>
            </div>

            <div className="row mt-3">
              {AVAILABLE_FIELDS.map(field => (
                <div key={field.key} className="col-md-4 mb-3">
                  <Droppable
                    field={field}
                    item={fieldMapping?.[field.key]}
                    handleManualRemove={handleManualRemove}
                    fieldCustomNames={fieldCustomNames}
                  />
                </div>
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeId ? (
                <Draggable id={activeId} originalId={parseDraggableId(activeId)} />
              ) : null}
            </DragOverlay>
          </DndContext>

          <div className="alert alert-info mt-3">
            <span className="d-block font-weight-bold text-big mb-2">{editableTranslate('user_import.mapping_progress', 'Zuordnungsfortschritt')}</span>
            <span dangerouslySetInnerHTML={{
              __html: sanitize(
                translate('user_import.x_of_y_columns_mapped', '$X von $Y Spalten zugeordnet')
                  .replace('$X', mappedCount)
                  .replace('$Y', importData?.headers?.length ?? 0)
              )
            }} />
          </div>

          <div className="text-center mt-3">
            <Button variant="secondary" onClick={handleCancel} disabled={loading} data-testid="cancel-button">
              {editableTranslate('button.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSaveFieldMapping} loading={loading} data-testid="save-mapping-button" className="ml-2">{editableTranslate('user_import.save_mapping', 'Zuordnung speichern')}</Button>
          </div>
        </div>
      )}

      {/* Field mapping — summary */}
      {(forTable || activeDetailTab === "mapping") && !isDraft && (
        <div className="mt-4">
          <div className="import-field-assignment">
            <span>{editableTranslate('user_import.field_assignment', 'Feldzuordnungen')}</span>
            {reverseFieldMapping.length > 0 ? (
              <table className="table" data-testid="reverse-field-mapping-table">
                <tbody>
                  {reverseFieldMapping.map((item, i) => (
                    <tr key={i} data-testid={`row-${item?.translation_key}`}>
                      <td className="w-30percent">{item.header}</td>
                      <td className="w-40percent text-center"><i className="bi bi-arrow-right"></i></td>
                      <td className="w-30percent text-right">{fieldCustomNames?.[item.translation_key] || editableTranslate(item.translation_key, item.label)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="alert alert-light border mt-3 mb-0" data-testid="empty-state">
                {editableTranslate('global.no_data', 'Keine Daten')}
              </div>
            )}
          </div>

          {isReadyForImport && (
            <div className="text-center mt-3">
              <Button variant="secondary" onClick={handleCancel} disabled={loading} data-testid="cancel-button" className="ml-2">{editableTranslate('button.cancel')}</Button>
              <Button variant="secondary" onClick={handleDraft} disabled={loading} data-testid="back-button" className="ml-2">
                {editableTranslate('button.back')}
              </Button>
              <Button variant="primary" onClick={handleProcess} loading={loading} data-testid="process-button" className="ml-2">{editableTranslate('user_import.process', 'Verarbeiten')}</Button>
            </div>
          )}
        </div>
      )}

      {/* Records table */}
      {(forTable || activeDetailTab === "records") && isFinished && (
        <div>
          {!forTable && (
            <div className="alert alert-info mb-3">
              <i className="bi bi-info-circle mr-2"></i>
              {editableTranslate('user_import.role_assignment_info', 'Sie können nun mit der Rollenzuordnung fortfahren.')}{' '}
              <a href="/employee-management?tab=login_users" data-testid="login-users-link">
                {editableTranslate('user_import.go_to_login_users', 'Zu den Login-Benutzern')}
              </a>
            </div>
          )}

          {loadingRecords ? (
            <div className="d-flex justify-content-center py-3" data-testid="loading">
              <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only">Wird geladen...</span></div>
            </div>
          ) : (
            <>
              <div className="table-responsive has-custom-scrollbar">
                <table className="table common-table" ref={tableRef} data-testid="records-table">
                  <thead>
                    <tr>
                      <th>{fieldCustomNames?.['field.first_name'] || editableTranslate('user_import.first_name', 'Vorname')}</th>
                      <th>{fieldCustomNames?.['field.last_name'] || editableTranslate('user_import.last_name', 'Nachname')}</th>
                      <th>{fieldCustomNames?.['field.email'] || editableTranslate('user_import.email', 'E-Mail')}</th>
                      <th>{fieldCustomNames?.['field.employee_number'] || editableTranslate('field.employee_number', 'Personalnummer')}</th>
                      <th>{editableTranslate('user_import.status', 'Status')}</th>
                      <th>{editableTranslate('user_import.operation', 'Aktion')}</th>
                      <th>{editableTranslate('user_import.log', 'Protokoll')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center" data-testid="empty-state">
                          {editableTranslate('global.no_data', 'Keine Daten')}
                        </td>
                      </tr>
                    )}
                    {records.map((record, i) => {
                      const firstNameKey = Object.keys(record.data || {}).find(k => fieldMapping['first_name'] === k) ?? Object.keys(record.data || {})[0];
                      const lastNameKey = Object.keys(record.data || {}).find(k => fieldMapping['last_name'] === k) ?? Object.keys(record.data || {})[1];
                      const emailKey = Object.keys(record.data || {}).find(k => fieldMapping['email'] === k) ?? Object.keys(record.data || {})[2];
                      const employeeNumberKey = fieldMapping['employee_number'];

                      const logMessages = Array.isArray(record.log) ? record.log : [];

                      return (
                        <tr key={i} className={recordRowClass(record.status)} data-testid={`record-row-${i}`}>
                          <td>{record.data?.[firstNameKey] ?? '-'}</td>
                          <td>{record.data?.[lastNameKey] ?? '-'}</td>
                          <td>{record.data?.[emailKey] ?? '-'}</td>
                          <td>{(employeeNumberKey && record.data?.[employeeNumberKey]) || '-'}</td>
                          <td>
                            <span className={`badge ${recordBadgeClass(record.status)}`}>
                              {editableTranslate('user_import.operation_status.' + record.status, operationStatusAlts[record.status] ?? '')}
                            </span>
                          </td>
                          <td>{record?.operation ? editableTranslate('user_import.operation.' + record?.operation, operationAlts[record?.operation] ?? '') : '-'}</td>
                          <td>
                            {logMessages.length > 0 ? (
                              <ul className="mb-0 pl-3">
                                {logMessages.map((log, j) => (
                                  <li key={j}>
                                    <span dangerouslySetInnerHTML={{ __html: sanitize(log.fallback ?? log.translation_key) }} />
                                  </li>
                                ))}
                              </ul>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <TableScrollbar tableRef={tableRef} data={records} trigger={resizedCount} />
            </>
          )}
        </div>
      )}

      {(forTable || activeDetailTab === "records") && !isFinished && (
        <div className="alert alert-light border mt-4">
          {editableTranslate('user_import.records_available_after_processing', 'Die Datensätze werden hier angezeigt, nachdem der Import verarbeitet wurde.')}
        </div>
      )}

      {isFinished && !forTable && (
        <div className="text-center mt-3">
          <Button variant="secondary" onClick={handleReturnToOverview} data-testid="back-button">
            {editableTranslate('button.back')}
          </Button>
        </div>
      )}
    </div>
  );
}
